import React, { useRef, useState } from "react";
import { CustomFormField } from "components/custom-form/CustomForm";
import { DropdownOption } from "components/searchable-dropdown/types";
import merchant_services from "services/merchant_services";
import { COUNTRIES, countryOptions } from "data/countries";
import { countryCodeFromMerchantId } from "utils/merchantId";
import { mccOptions } from "data/mcc";
import {
  MerchantBusinessType,
  MoneySettlementPlatforms,
  SettlementCountry,
} from "types/moneySettlement";
import { useSelector } from "react-redux";
import {
  networkTypeSelector,
  treasuryIdSelector,
} from "store/userSliceHelpers";
import verification_services from "services/verification_services";
import { apiErrorMessage } from "utils/errors/parseApiError";
import {
  OTP_LENGTH,
  classifyOtpFailure,
  classifyPreflightFailure,
  classifySignupFailure,
  sanitizeOtp,
  useResendCountdown,
} from "hooks/useEmailOtp";

const EMAIL_RE = /\S+@\S+\.\S+/;

const BUSINESS_TYPE_OPTIONS: DropdownOption<MerchantBusinessType>[] = [
  { value: "individual", label: "Individual" },
  { value: "company", label: "Company" },
];

const STRIPE_OPTION: DropdownOption<MoneySettlementPlatforms> = {
  value: "stripe",
  label: "Stripe",
};
// const COLUMN_OPTION: DropdownOption<MoneySettlementPlatforms> = {
//   value: "column",
//   label: "Column",
// };

export type AddMerchantStep = 1 | 2 | 3;

export interface UseAddMerchantReturn {
  step: AddMerchantStep;
  /** 2 when the preflight skipped the email gate, 3 otherwise. */
  totalSteps: number;
  /** True when resuming signup for an already-on-chain merchant (no step 1). */
  isResume: boolean;
  fields: CustomFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  submitLabel: string;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  /** False when re-submitting cannot help — a dead code, or the guess cap. */
  canRetry: boolean;
  showSuccess: boolean;
  successMsg: string;
  /** Non-blocking note under the form, e.g. that a resend invalidated the previous code. */
  noticeMsg: string;
  /** Resend affordance for step 3. */
  canResend: boolean;
  secondsLeft: number;
  handleResend: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  /** Retry the current step, dismissing the error first so a success cannot leave it standing. */
  handleRetry: () => Promise<void>;
  /** Step 3 → 2, so a mistyped email can be corrected. Null when there is nowhere to go back to. */
  handleBack: (() => void) | null;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
  reset: () => void;
}

export function useAddMerchant(
  resumeMerchantId?: string,
): UseAddMerchantReturn {
  // Resume mode: a merchant registered on-chain (step 1) but never completed
  // Firebase signup. Jump straight to step 2 with the merchantId seeded and the
  // country derived from it. (AddMerchantForm remounts the hook per open, so
  // these initializers re-run with a fresh resumeMerchantId each time.)
  const isResume = !!resumeMerchantId;
  const [step, setStep] = useState<AddMerchantStep>(isResume ? 2 : 1);

  // Step 1 — create merchant
  const [merchantName, setMerchantName] = useState("");
  const [country, setCountry] = useState<number | null>(
    isResume ? countryCodeFromMerchantId(resumeMerchantId!) : null,
  );
  const [mcc, setMcc] = useState<number | null>(null);
  const [isManagedAccount, setIsManagedAccount] = useState(true);

  // Step 2 — sign up merchant
  const [merchantId, setMerchantId] = useState(resumeMerchantId ?? "");
  const [email, setEmail] = useState("");
  const [merchantDesc, setMerchantDesc] = useState("");
  // Business type is fixed to "individual" for now (non-editable dropdown).
  const [businessType, setBusinessType] =
    useState<MerchantBusinessType>("individual");
  const [platformId, setPlatformId] = useState<MoneySettlementPlatforms | null>(
    "stripe",
  );
  // Settlement mode: checked ⇒ "test" (safe default), unchecked ⇒ "live".

  // Step 3 — email OTP gate
  const [otp, setOtp] = useState("");

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [canRetry, setCanRetry] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [noticeMsg, setNoticeMsg] = useState("");
  const {
    secondsLeft,
    start: startResendCountdown,
    stop: stopResendCountdown,
  } = useResendCountdown();

  /** Set once /verifyOtp succeeds, so a retry after a signup-side failure skips re-verifying. */
  const emailVerifiedRef = useRef(false);
  /** Bumped by reset(); async actions drop their setState calls if it moved. See useIssueCard. */
  const runIdRef = useRef(0);

  const treasuryId = useSelector(treasuryIdSelector);
  const networkType = useSelector(networkTypeSelector);
  const merchantNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const merchantDescRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const filteredCountryOptions =
    networkType === "mainnet"
      ? countryOptions.filter((country) => country.code === "840")
      : countryOptions;

  const isStale = (run: number) => runIdRef.current !== run;

  const clearError = () => {
    setShowError(false);
    setErrorMsg("");
    setCanRetry(true);
  };

  const openError = (msg: string, retryable = true) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setCanRetry(retryable);
    setShowError(true);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const reset = () => {
    // Anything already awaiting a response belongs to the previous run from here on.
    runIdRef.current += 1;
    setStep(1);
    setMerchantName("");
    setCountry(null);
    setMcc(null);
    setIsManagedAccount(true);
    setMerchantId("");
    setEmail("");
    setMerchantDesc("");
    setBusinessType("individual");
    setPlatformId(null);
    setOtp("");
    setNoticeMsg("");
    stopResendCountdown();
    emailVerifiedRef.current = false;
    // Overlays too: one left standing outlives the form that opened it and blocks the page.
    clearError();
    setShowLoading(false);
    setLoadingMsg("");
    setShowSuccess(false);
    setSuccessMsg("");
  };

  // Step 1 stores the ISO 3166-1 NUMERIC country code; the settlement API needs
  // the alpha-2 ("US" / "GR"). Resolve it from the shared country table.
  const countryAlpha2 = (COUNTRIES.find((c) => c.code === country)?.alpha2 ??
    "") as SettlementCountry | "";

  // Column is US-only; Greece (and anything else) settles via Stripe only.
  const platformOptions: DropdownOption<MoneySettlementPlatforms>[] = [
    STRIPE_OPTION,
  ];
  // countryAlpha2 === "US" ? [STRIPE_OPTION, COLUMN_OPTION] : [STRIPE_OPTION];

  // ── Step 1 ────────────────────────────────────────────────────────────────
  const submitStep1 = async () => {
    if (!merchantName.trim()) return openError("Merchant name is required.");
    if (country === null) return openError("Please select a country.");
    if (mcc === null)
      return openError("Please select a merchant category (MCC).");

    const run = runIdRef.current;
    try {
      setLoadingMsg("Adding merchant…");
      setShowLoading(true);

      const res = await merchant_services.addMerchant(
        {
          treasuryId: treasuryId ?? "",
          merchantName: merchantName.trim(),
          country,
          mcc,
          // "Managed account" checked ⇒ internally managed ⇒ not external.
          isExternal: !isManagedAccount,
        },
        networkType,
      );

      if (isStale(run)) return;
      setMerchantId(res.merchantId);
      setShowLoading(false);
      setStep(2);
    } catch (err: any) {
      if (isStale(run)) return;
      openError(
        apiErrorMessage(err, "Failed to add merchant. Please try again."),
      );
    }
  };

  // ── Step 2 — collect the signup details, then mail a code to the merchant ──
  const submitStep2 = async () => {
    if (!EMAIL_RE.test(email))
      return openError("Please enter a valid email address.");
    if (!countryAlpha2) return openError("Merchant country is missing.");
    if (!platformId)
      return openError("Please select a money settlement platform.");

    const run = runIdRef.current;

    // ── Leg 1: the sanity checks ──
    // Preflight FIRST, always: the other order mails a code before discovering the merchant id is
    // already registered, or registered to somebody else.
    let decision: "verify_email";
    try {
      setLoadingMsg("Checking merchant…");
      setShowLoading(true);
      const result = await merchant_services.prepareSignup({
        merchantId,
        email: email.trim(),
        merchantDesc: merchantDesc.trim(),
        platformId,
        country: countryAlpha2,
        settlementMode: networkType === "mainnet" ? "live" : "test",
      });
      if (isStale(run)) return;
      decision = result.decision;
    } catch (err: any) {
      if (isStale(run)) return;
      const { isTerminal, message } = classifyPreflightFailure(
        err,
        "Couldn't check that merchant. Please try again.",
      );
      // Terminal: the operator must change the merchant or the email, and the form behind the
      // modal is still holding both.
      return openError(message, !isTerminal);
    }

    // A frontend deployed ahead of the backend would otherwise mail a code on a stale decision.
    if (decision !== "verify_email") {
      return openError("Couldn't check that merchant. Please try again.", false);
    }

    // ── Leg 2: prove the inbox before binding a payout destination ──
    try {
      setLoadingMsg("Sending verification code…");
      setShowLoading(true);
      await verification_services.verifyEmail(
        { email: email.trim() },
        networkType,
      );
      if (isStale(run)) return;
      setShowLoading(false);
      setOtp("");
      setNoticeMsg(`We sent a ${OTP_LENGTH}-digit code to ${email.trim()}.`);
      startResendCountdown();
      setStep(3);
    } catch (err: any) {
      if (isStale(run)) return;
      openError(
        apiErrorMessage(
          err,
          "Failed to send the verification code. Please try again.",
        ),
      );
    }
  };

  // ── Resend — replaces the previous code, so say so ────────────────────────
  const handleResend = async () => {
    const run = runIdRef.current;
    clearError();
    try {
      setLoadingMsg("Sending a new code…");
      setShowLoading(true);
      await verification_services.verifyEmail(
        { email: email.trim() },
        networkType,
      );
      if (isStale(run)) return;
      setShowLoading(false);
      // The server overwrites the record, so the earlier code stops working immediately. Saying
      // this is what stops someone typing a slow-arriving first email and burning a guess.
      setOtp("");
      setNoticeMsg(
        "A new code is on its way. Use the most recent email — the previous code no longer works.",
      );
      startResendCountdown();
      emailVerifiedRef.current = false;
    } catch (err: any) {
      if (isStale(run)) return;
      const { code, message } = classifyOtpFailure(
        err,
        "Failed to resend the code. Please try again.",
      );
      // The server's own 60s floor — keep the countdown running rather than leaving the button
      // enabled while every press is refused.
      if (code === "RATE_LIMITED" && secondsLeft === 0) startResendCountdown();
      // Not retryable through the modal: its retry runs handleSubmit, which would submit the OTP
      // form rather than resend. The resend control is already on screen.
      openError(message, false);
    }
  };

  // ── Step 3 — check the code, then complete signup ─────────────────────────
  const submitStep3 = async () => {
    if (otp.trim().length !== OTP_LENGTH)
      return openError(`Please enter the ${OTP_LENGTH}-digit code.`);
    if (!countryAlpha2) return openError("Merchant country is missing.");
    if (!platformId)
      return openError("Please select a money settlement platform.");

    const run = runIdRef.current;

    // ── Leg 1: the code. Only here can a failure mean "wrong/dead OTP". ──
    if (!emailVerifiedRef.current) {
      try {
        setLoadingMsg("Verifying code…");
        setShowLoading(true);
        await verification_services.verifyOtp(
          { email: email.trim(), otp: otp.trim() },
          networkType,
        );
        if (isStale(run)) return;
        emailVerifiedRef.current = true;
      } catch (err: any) {
        if (isStale(run)) return;
        const { isWrongCode, needsNewCode, message } = classifyOtpFailure(
          err,
          "Failed to verify the code. Please try again.",
        );
        if (isWrongCode) {
          // Clear it: the modal's retry would resend the same wrong digits and spend another of
          // the five server-side guesses.
          setOtp("");
          otpRef.current?.focus();
          return openError(message, false);
        }
        if (needsNewCode) {
          setOtp("");
          stopResendCountdown();
          setNoticeMsg("That code can no longer be used. Request a new one.");
          return openError(message, false);
        }
        return openError(message);
      }
    }

    // ── Leg 2: signup. A failure here is NOT an OTP problem, and must not be read as one —
    // RATE_LIMITED here is Firebase Auth throttling, and treating it as the guess cap would throw
    // away a verification that is still valid.
    try {
      setLoadingMsg("Completing signup…");
      setShowLoading(true);
      await merchant_services.signUpMerchant({
        merchantId,
        email: email.trim(),
        merchantDesc: merchantDesc.trim(),
        platformId,
        country: countryAlpha2,
        businessType,
        // Follows the deploy's chain: mainnet settles live, testnet sandbox.
        settlementMode: networkType === "mainnet" ? "live" : "test",
      });
      if (isStale(run)) return;
      setShowLoading(false);
      setNoticeMsg("");
      stopResendCountdown();
      setSuccessMsg(
        "Merchant has been added successfully. A temporary password was sent to the merchant's email.",
      );
      setShowSuccess(true);
    } catch (err: any) {
      if (isStale(run)) return;
      const { needsNewCode, isTerminal, message } = classifySignupFailure(
        err,
        "Failed to complete signup. Please try again.",
      );
      if (needsNewCode) {
        // The verification lapsed or was spent — re-verifying is the only way forward.
        setOtp("");
        stopResendCountdown();
        emailVerifiedRef.current = false;
        setNoticeMsg("That code can no longer be used. Request a new one.");
        return openError(message, false);
      }
      openError(message, !isTerminal);
    }
  };

  const handleSubmit = async () => {
    // Any new attempt supersedes the last failure; otherwise a successful retry leaves the error
    // modal standing, and it is only ever dismissed by its own Close button.
    clearError();
    if (step === 1) await submitStep1();
    else if (step === 2) await submitStep2();
    else await submitStep3();
  };

  /**
   * The error modal's retry. Dismisses before resubmitting: a retry that succeeds advances the
   * step, and a modal still open then has its "Try again" bound to the NEXT step's action.
   */
  const handleRetry = async () => {
    clearError();
    await handleSubmit();
  };

  /**
   * Back to the details step. Required, not a nicety: a fresh flow cannot be closed from here
   * (the merchant is on-chain but unsigned) and the email is read-only, so without this a
   * mistyped address is a dead end — resend only re-sends to the same wrong inbox.
   *
   * Abandons the outstanding code deliberately. It stays valid server-side until it expires, and
   * sending to a corrected address issues a fresh one anyway.
   */
  const handleBack = () => {
    runIdRef.current += 1;
    setOtp("");
    setNoticeMsg("");
    stopResendCountdown();
    emailVerifiedRef.current = false;
    clearError();
    setStep(2);
  };

  const focusNext =
    (ref: React.RefObject<HTMLInputElement>) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter") ref.current?.focus();
    };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const step1Fields: CustomFormField[] = [
    {
      kind: "text",
      name: "merchantName",
      label: "Merchant Name",
      value: merchantName,
      onChange: setMerchantName,
      placeholder: "Business display name",
      autoComplete: "off",
      inputRef: merchantNameRef,
      preserveSpaces: true,
    },
    {
      kind: "select",
      name: "country",
      label: "Country",
      value: country,
      onChange: setCountry,
      options: filteredCountryOptions,
      placeholder: "Select a country",
      searchPlaceholder: "Search by name or code",
    },
    {
      kind: "select",
      name: "mcc",
      label: "Merchant Category (MCC)",
      value: mcc,
      onChange: setMcc,
      options: mccOptions,
      placeholder: "Select a category",
      searchPlaceholder: "Search by name or code",
    },
    {
      kind: "checkbox",
      name: "isManagedAccount",
      label: "Managed account",
      checked: isManagedAccount,
      onChange: setIsManagedAccount,
    },
  ];

  const step2Fields: CustomFormField[] = [
    {
      kind: "text",
      name: "merchantId",
      label: "Merchant ID",
      value: merchantId,
      onChange: () => {},
      readOnly: true,
      autoComplete: "off",
    },
    {
      kind: "email",
      name: "email",
      label: "Email",
      value: email,
      onChange: setEmail,
      onKeyDown: focusNext(merchantDescRef),
      placeholder: "merchant@email.com",
      autoComplete: "email",
      inputRef: emailRef,
    },
    {
      kind: "text",
      name: "merchantDesc",
      label: "Merchant Description",
      value: merchantDesc,
      onChange: setMerchantDesc,
      onKeyDown: submitOnEnter,
      placeholder: "Short description",
      autoComplete: "off",
      inputRef: merchantDescRef,
      preserveSpaces: true,
    },
    {
      // Derived from the step-1 country as its alpha-2 code; non-editable.
      kind: "text",
      name: "country",
      label: "Country",
      value: countryAlpha2,
      onChange: () => {},
      readOnly: true,
      autoComplete: "off",
    },
    {
      // Fixed to "individual" for now; rendered as a disabled dropdown.
      kind: "select",
      name: "businessType",
      label: "Business Type",
      value: businessType,
      onChange: (val: string) => setBusinessType(val as MerchantBusinessType),
      options: BUSINESS_TYPE_OPTIONS,
      disabled: true,
    },
    // {
    //   kind: "select",
    //   name: "platformId",
    //   label: "Money Settlement Platform",
    //   value: platformId,
    //   onChange: (val: string) => setPlatformId(val as MoneySettlementPlatforms),
    //   options: platformOptions,
    //   placeholder: "Select a platform",
    //   disabled: true,
    // },
  ];

  const step3Fields: CustomFormField[] = [
    {
      kind: "email",
      name: "email",
      label: "Email",
      value: email,
      onChange: () => {},
      readOnly: true,
      autoComplete: "off",
    },
    {
      kind: "text",
      name: "otp",
      label: "Verification Code",
      value: otp,
      onChange: (val) => setOtp(sanitizeOtp(val)),
      onKeyDown: submitOnEnter,
      placeholder: "12345",
      autoComplete: "one-time-code",
      inputRef: otpRef,
    },
  ];

  const fieldsByStep: Record<AddMerchantStep, CustomFormField[]> = {
    1: step1Fields,
    2: step2Fields,
    3: step3Fields,
  };

  const submitLabelByStep: Record<AddMerchantStep, string> = {
    1: "Continue",
    // Not "Send Code": whether a code is sent is the preflight's answer.
    2: "Continue",
    3: "Verify & Complete",
  };

  const isSubmitDisabled =
    step === 1
      ? !merchantName.trim() || country === null || mcc === null
      : step === 2
        ? !EMAIL_RE.test(email) ||
          !platformId ||
          !countryAlpha2 ||
          // .trim() to match merchantName in step 1: without it a whitespace-only
          // description enables submit and is then sent as an empty string.
          !merchantDesc.trim()
        : otp.length !== OTP_LENGTH;

  const totalSteps = 3;

  return {
    step,
    totalSteps,
    isResume,
    fields: fieldsByStep[step],
    submitRef,
    submitLabel: submitLabelByStep[step],
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    canRetry,
    showSuccess,
    successMsg,
    noticeMsg,
    canResend: step === 3 && secondsLeft === 0,
    secondsLeft,
    handleResend,
    handleSubmit,
    handleRetry,
    handleBack: step === 3 ? handleBack : null,
    handleCloseError,
    handleCloseSuccess,
    reset,
  };
}
